/**
 * Profile Manager Module
 * Handles profile CRUD operations and system-config storage
 * Extracted from csvImportApiClient.js for better maintainability
 */

import mockDataService from '../mockDataService';

// Environment configuration
const USE_MOCK_DATA = process.env.REACT_APP_API_MODE === 'mock';
const DEBUG_API = process.env.REACT_APP_DEBUG_API === 'true';

class ProfileManager {
    constructor(baseApiClient) {
        this.baseClient = baseApiClient;
        this.api = baseApiClient.api; // Keep for backward compatibility
    }

    /**
     * System-Config key for storing all profiles as single JSON array
     * NEW APPROACH: Single config entry instead of multiple keys
     */
    static get PROFILES_CONFIG_KEY() {
        return 'csvImportProfiles';
    }

    /**
     * Get all import profiles
     * @returns {Object} Response with profiles data
     */
    async getProfiles() {
        console.log('[ProfileManager] getProfiles called');
        console.log('[ProfileManager] REACT_APP_API_MODE:', process.env.REACT_APP_API_MODE);
        console.log('[ProfileManager] USE_MOCK_DATA:', USE_MOCK_DATA);
        
        if (USE_MOCK_DATA) {
            const mockResult = await mockDataService.getProfiles();
            console.log('[ProfileManager] Mock profiles result:', mockResult);
            return mockResult;
        }
        
        try {
            // Ensure authentication before API call
            await this.baseClient.ensureAuthenticated();
            
            // NEW APPROACH: Load single system-config entry containing all profiles
            // FIXED: Sort by updatedAt to get the LATEST version, not first/oldest
            const response = await this.api.post('/search/system-config', {
                filter: [
                    {
                        type: 'equals',
                        field: 'configurationKey',
                        value: ProfileManager.PROFILES_CONFIG_KEY
                    }
                ],
                sort: [
                    { field: 'updatedAt', order: 'DESC' }
                ],
                limit: 1
            });
            
            const configEntries = response.data?.data || [];
            let profiles = [];
            
            // Store the system-config ID for future updates (CRITICAL FIX)
            this.currentConfigId = configEntries.length > 0 ? configEntries[0].id : null;
            
            if (configEntries.length > 0) {
                try {
                    const profilesData = JSON.parse(configEntries[0].configurationValue || '[]');
                    
                    if (DEBUG_API) {
                        console.log('[Profiles] Found profiles array:', profilesData.length);
                    }
                    
                    // DEDUPLICATE profiles by ID (fix for existing bad data)
                    const profileMap = new Map();
                    profilesData.forEach(profileData => {
                        const profileId = profileData.id;
                        if (!profileMap.has(profileId) || 
                            new Date(profileData.updatedAt || 0) > new Date(profileMap.get(profileId).updatedAt || 0)) {
                            profileMap.set(profileId, profileData);
                        }
                    });
                    
                    // Ensure all profiles have required structure
                    profiles = Array.from(profileMap.values()).map(profileData => ({
                        id: profileData.id,
                        name: profileData.name || 'Unnamed Profile',
                        description: profileData.description || '',
                        importType: profileData.importType || 'singleCustomerMultipleOrders',
                        fieldMapping: profileData.fieldMapping || {},
                        defaultValues: profileData.defaultValues || {},
                        countryShippingMethods: profileData.countryShippingMethods || [],
                        // Include customer and address data from saved profiles
                        customer: profileData.customer || null,
                        billingAddress: profileData.billingAddress || null,
                        shippingAddress: profileData.shippingAddress || null,
                        csvHeaders: profileData.csvHeaders || null,
                        createdAt: profileData.createdAt || new Date().toISOString(),
                        updatedAt: profileData.updatedAt || new Date().toISOString()
                    })).sort((a, b) => new Date(b.updatedAt) - new Date(a.updatedAt));
                    
                    if (DEBUG_API) {
                        console.log(`[Profiles] Deduplicated: ${profilesData.length} entries -> ${profiles.length} unique profiles`);
                    }
                    
                    // If we deduplicated, save the clean data back to database
                    if (profilesData.length !== profiles.length) {
                        console.log('[Profiles] Saving deduplicated profiles back to database...');
                        try {
                            await this.saveProfilesToConfig(profiles);
                            console.log('[Profiles] Successfully saved deduplicated profiles');
                        } catch (saveError) {
                            console.error('[Profiles] Failed to save deduplicated profiles:', saveError);
                        }
                    }
                    
                } catch (parseError) {
                    console.warn('[Profiles] Error parsing profiles JSON:', parseError);
                    profiles = [];
                }
            } else {
                if (DEBUG_API) {
                    console.log('[Profiles] No profiles config found, checking for old format profiles to migrate');
                }
                
                // Try to migrate old format profiles (csvImportProfile.{id}) - ONLY ONCE
                try {
                    // Check if migration already happened by looking for old profiles
                    const oldCheckResponse = await this.api.post('/search/system-config', {
                        filter: [{
                            type: 'prefix',
                            field: 'configurationKey',
                            value: 'csvImportProfile.'
                        }],
                        limit: 1
                    });
                    
                    if (oldCheckResponse.data?.data?.length > 0) {
                        console.log('[Profiles] Found old format profiles, starting migration...');
                        profiles = await this.migrateOldFormatProfiles();
                    } else {
                        console.log('[Profiles] No old profiles found, starting with empty array');
                        profiles = [];
                    }
                } catch (migrationError) {
                    console.error('[Profiles] Migration failed:', migrationError);
                    profiles = [];
                }
            }
            
            if (DEBUG_API) {
                console.log('[Profiles] Processed profiles:', profiles.length);
                profiles.forEach(p => console.log(`  - ${p.name} (${p.id})`));
            }
            
            return {
                success: true,
                data: profiles,
                total: profiles.length
            };
            
        } catch (error) {
            console.error('Error fetching profiles:', error);
            return {
                success: false,
                data: [],
                total: 0,
                error: error.response?.data?.errors?.[0]?.detail || error.message
            };
        }
    }

    /**
     * Get import profiles (alias for getProfiles for backward compatibility)
     * @returns {Object} Response with profiles data
     */
    async getImportProfiles() {
        return this.getProfiles();
    }

    /**
     * Create a new import profile
     * @param {Object} profileData - Profile data to create
     * @returns {Object} Response with created profile
     */
    async createImportProfile(profileData) {
        if (USE_MOCK_DATA) {
            return await mockDataService.createImportProfile(profileData);
        }
        
        try {
            // Ensure authentication before API call
            await this.baseClient.ensureAuthenticated();
            
            // Generate unique ID for the profile
            const profileId = this.generateProfileId();
            
            // Prepare profile with metadata
            const profile = {
                ...profileData,
                id: profileId,
                createdAt: new Date().toISOString(),
                updatedAt: new Date().toISOString()
            };
            
            // NEW APPROACH: Load all profiles, add new one, save all
            const allProfilesResult = await this.getProfiles();
            const allProfiles = allProfilesResult.success ? allProfilesResult.data : [];
            
            // Add new profile to array
            allProfiles.push(profile);
            
            // Save entire profiles array using proper UPDATE method
            await this.saveProfilesToConfig(allProfiles);
            
            if (DEBUG_API) {
                console.log('[Profiles] Created profile:', profileId, profile.name);
            }
            
            return {
                success: true,
                data: profile,
                message: 'Profil erfolgreich erstellt'
            };
            
        } catch (error) {
            console.error('Error creating profile:', error);
            return {
                success: false,
                error: error.response?.data?.errors?.[0]?.detail || error.message
            };
        }
    }

    /**
     * Update an existing import profile
     * @param {string} profileId - Profile ID to update
     * @param {Object} profileData - Updated profile data
     * @returns {Object} Response with updated profile
     */
    async updateImportProfile(profileId, profileData) {
        if (USE_MOCK_DATA) {
            return await mockDataService.updateImportProfile(profileId, profileData);
        }
        
        try {
            // Ensure authentication before API call
            await this.baseClient.ensureAuthenticated();
            
            // Prepare updated profile with metadata
            const profile = {
                ...profileData,
                id: profileId,
                updatedAt: new Date().toISOString()
            };
            
            // Preserve createdAt if it exists
            if (!profile.createdAt) {
                profile.createdAt = new Date().toISOString();
            }
            
            // NEW APPROACH: Load all profiles, update specific one, save all
            const allProfilesResult = await this.getProfiles();
            const allProfiles = allProfilesResult.success ? allProfilesResult.data : [];
            
            // Find and update the specific profile
            const profileIndex = allProfiles.findIndex(p => p.id === profileId);
            if (profileIndex !== -1) {
                allProfiles[profileIndex] = profile;
            } else {
                // Profile not found, add it (fallback)
                allProfiles.push(profile);
            }
            
            // Save entire profiles array using proper UPDATE method
            await this.saveProfilesToConfig(allProfiles);
            
            if (DEBUG_API) {
                console.log('[Profiles] Updated profile:', profileId, profile.name);
            }
            
            return {
                success: true,
                data: profile,
                message: 'Profil erfolgreich aktualisiert'
            };
            
        } catch (error) {
            console.error('Error updating profile:', error);
            return {
                success: false,
                error: error.response?.data?.errors?.[0]?.detail || error.message
            };
        }
    }

    /**
     * Delete an import profile
     * @param {string} profileId - Profile ID to delete
     * @returns {Object} Response indicating success/failure
     */
    async deleteImportProfile(profileId) {
        if (USE_MOCK_DATA) {
            return await mockDataService.deleteImportProfile(profileId);
        }
        
        try {
            // Ensure authentication before API call
            await this.baseClient.ensureAuthenticated();
            
            // NEW APPROACH: Load all profiles, remove specific one, save remaining
            const allProfilesResult = await this.getProfiles();
            const allProfiles = allProfilesResult.success ? allProfilesResult.data : [];
            
            // Filter out the profile to delete
            const filteredProfiles = allProfiles.filter(p => p.id !== profileId);
            
            if (DEBUG_API) {
                console.log(`[Profiles] Removing profile ${profileId}. Before: ${allProfiles.length}, After: ${filteredProfiles.length}`);
            }
            
            // Save remaining profiles array using proper UPDATE method
            await this.saveProfilesToConfig(filteredProfiles);
            
            if (DEBUG_API) {
                console.log('[Profiles] Successfully deleted profile from array:', profileId);
            }
            
            return {
                success: true,
                message: 'Profil erfolgreich gelöscht'
            };
            
        } catch (error) {
            console.error('Error deleting profile:', error);
            return {
                success: false,
                error: error.response?.data?.errors?.[0]?.detail || error.message
            };
        }
    }

    /**
     * Copy an existing import profile
     * @param {string} sourceProfileId - ID of profile to copy
     * @param {string} newProfileName - Name for the new profile
     * @returns {Object} Response with copied profile
     */
    async copyImportProfile(sourceProfileId, newProfileName) {
        if (USE_MOCK_DATA) {
            return await mockDataService.copyImportProfile(sourceProfileId, newProfileName);
        }
        
        try {
            // First, get the source profile
            const profilesResponse = await this.getProfiles();
            
            if (!profilesResponse.success) {
                return {
                    success: false,
                    error: 'Fehler beim Laden der Profile'
                };
            }
            
            const sourceProfile = profilesResponse.data.find(p => p.id === sourceProfileId);
            
            if (!sourceProfile) {
                return {
                    success: false,
                    error: 'Quellprofil nicht gefunden'
                };
            }
            
            // Create a copy with new ID and name
            const copiedProfileData = {
                ...sourceProfile,
                name: newProfileName,
                description: `Kopie von: ${sourceProfile.name}`
            };
            
            // Remove ID and timestamps so they get regenerated
            delete copiedProfileData.id;
            delete copiedProfileData.createdAt;
            delete copiedProfileData.updatedAt;
            
            // Create the new profile
            return await this.createImportProfile(copiedProfileData);
            
        } catch (error) {
            console.error('Error copying profile:', error);
            return {
                success: false,
                error: error.response?.data?.errors?.[0]?.detail || error.message
            };
        }
    }

    /**
     * Generate a unique profile ID
     * @returns {string} Unique profile ID
     */
    generateProfileId() {
        const timestamp = Date.now();
        const randomNum = Math.floor(Math.random() * 1000);
        return `profile_${timestamp}_${randomNum}`;
    }

    /**
     * Migrate old format profiles (csvImportProfile.{id}) to new format (single array)
     * @returns {Array} Migrated profiles array
     */
    async migrateOldFormatProfiles() {
        try {
            if (DEBUG_API) {
                console.log('[Migration] Looking for old format profiles...');
            }
            
            // Search for old format profiles
            const response = await this.api.post('/search/system-config', {
                filter: [
                    {
                        type: 'prefix',
                        field: 'configurationKey',
                        value: 'csvImportProfile.'
                    }
                ],
                limit: 100
            });
            
            const oldConfigEntries = response.data?.data || [];
            
            if (oldConfigEntries.length === 0) {
                if (DEBUG_API) {
                    console.log('[Migration] No old format profiles found');
                }
                return [];
            }
            
            console.log(`[Migration] Found ${oldConfigEntries.length} old format profiles, migrating...`);
            
            const profileMap = new Map(); // For deduplication
            
            oldConfigEntries.forEach(configEntry => {
                try {
                    // Skip null/empty entries
                    if (!configEntry.configurationValue || 
                        configEntry.configurationValue === 'null' || 
                        configEntry.configurationValue === '' ||
                        configEntry.configurationValue === '""') {
                        return;
                    }
                    
                    const profileData = JSON.parse(configEntry.configurationValue);
                    
                    // Skip invalid data
                    if (!profileData || typeof profileData !== 'object') {
                        return;
                    }
                    
                    // Extract profile ID from key
                    const profileId = configEntry.configurationKey.replace('csvImportProfile.', '');
                    
                    // Migrate to new format
                    const migratedProfile = {
                        id: profileId,
                        name: profileData.name || 'Migrated Profile',
                        description: profileData.description || '',
                        importType: profileData.importType || 'singleCustomerMultipleOrders',
                        fieldMapping: profileData.fieldMapping || {},
                        defaultValues: profileData.defaultValues || {},
                        countryShippingMethods: profileData.countryShippingMethods || [],
                        customer: profileData.customer || null,
                        billingAddress: profileData.billingAddress || null,
                        shippingAddress: profileData.shippingAddress || null,
                        csvHeaders: profileData.csvHeaders || null,
                        createdAt: profileData.createdAt || new Date().toISOString(),
                        updatedAt: profileData.updatedAt || new Date().toISOString()
                    };
                    
                    // Use Map to handle potential duplicates (take the latest one)
                    if (!profileMap.has(profileId) || 
                        new Date(migratedProfile.updatedAt) > new Date(profileMap.get(profileId).updatedAt)) {
                        profileMap.set(profileId, migratedProfile);
                    }
                    
                } catch (parseError) {
                    console.warn('[Migration] Error parsing old profile:', configEntry.configurationKey, parseError);
                }
            });
            
            // Convert Map to Array (deduplicated profiles)
            const uniqueProfiles = Array.from(profileMap.values()).sort((a, b) => 
                new Date(b.updatedAt) - new Date(a.updatedAt)
            );
            
            console.log(`[Migration] Deduplicated: ${oldConfigEntries.length} entries -> ${uniqueProfiles.length} unique profiles`);
            
            // Save migrated profiles to new format
            if (uniqueProfiles.length > 0) {
                await this.saveProfilesToConfig(uniqueProfiles);
                
                console.log(`[Migration] Successfully migrated ${uniqueProfiles.length} profiles to new format`);
            }
            
            return uniqueProfiles;
            
        } catch (error) {
            console.error('[Migration] Error migrating old format profiles:', error);
            return [];
        }
    }

    /**
     * Save profiles array to system-config using proper UPDATE method
     * @param {Array} profiles - Profiles array to save
     * @returns {Promise} Save operation result
     */
    async saveProfilesToConfig(profiles) {
        try {
            const profilesJson = JSON.stringify(profiles);
            
            if (this.currentConfigId) {
                // UPDATE existing config entry using PATCH
                if (DEBUG_API) {
                    console.log(`[Profiles] UPDATING existing config entry ${this.currentConfigId}`);
                }
                
                await this.api.patch(`/system-config/${this.currentConfigId}`, {
                    configurationValue: profilesJson
                });
            } else {
                // CREATE new config entry using POST (only for first time)
                if (DEBUG_API) {
                    console.log('[Profiles] CREATING new config entry');
                }
                
                const response = await this.api.post('/system-config', {
                    configurationKey: ProfileManager.PROFILES_CONFIG_KEY,
                    configurationValue: profilesJson
                });
                
                // Store the new config ID for future updates
                this.currentConfigId = response.data?.data?.id || response.data?.id;
            }
            
            if (DEBUG_API) {
                console.log('[Profiles] Successfully saved profiles to config');
            }
            
        } catch (error) {
            console.error('[Profiles] Error saving profiles to config:', error);
            throw error;
        }
    }

    /**
     * Validate profile data structure
     * @param {Object} profileData - Profile data to validate
     * @returns {Object} Validation result
     */
    validateProfileData(profileData) {
        const errors = [];
        const warnings = [];
        
        // Required fields
        if (!profileData.name || profileData.name.trim() === '') {
            errors.push('Profilname ist erforderlich');
        }
        
        if (!profileData.importType) {
            errors.push('Import-Typ ist erforderlich');
        }
        
        // Validate import type
        const validImportTypes = [
            'singleCustomerMultipleOrders',
            'singleCustomerSingleOrderWithPrices',
            'singleCustomerSingleOrderWithoutPrices',
            'multiCustomerMultipleOrders'
        ];
        
        if (profileData.importType && !validImportTypes.includes(profileData.importType)) {
            errors.push('Ungültiger Import-Typ');
        }
        
        // Validate field mapping
        if (!profileData.fieldMapping || Object.keys(profileData.fieldMapping).length === 0) {
            warnings.push('Keine Feld-Zuordnung konfiguriert');
        }
        
        // Validate default values
        if (!profileData.defaultValues) {
            warnings.push('Keine Standard-Werte konfiguriert');
        } else {
            if (!profileData.defaultValues.salesChannelId) {
                warnings.push('Kein Verkaufskanal konfiguriert');
            }
            
            if (!profileData.defaultValues.paymentMethodId) {
                warnings.push('Keine Zahlungsart konfiguriert');
            }
            
            if (!profileData.defaultValues.shippingMethodId) {
                warnings.push('Keine Versandart konfiguriert');
            }
        }
        
        return {
            isValid: errors.length === 0,
            errors,
            warnings
        };
    }
}

export default ProfileManager;